import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import FlightIcon from "@mui/icons-material/Flight";
import HotelIcon from "@mui/icons-material/Hotel";
import HistoryIcon from "@mui/icons-material/History";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SourceIcon from '@mui/icons-material/Source';
import {
  getMonitoringSummary,
  getMonitoringHistory,
} from "../operations/monitoringService";

const ITEMS_PER_PAGE = 10;

interface SummaryRecord {
  product: string;
  supplierId: number;
  supplierName: string;
  supplierSource: string;
  records: number;
  responseTime: number;
  health: string;
  createdAt?: string; // Added to fix the error
}

interface SummaryResponse {
  status: number;
  data: {
    status: string;
    data: {
      flight: SummaryRecord[];
      hotel: SummaryRecord[];
    };
  };
}

interface HistoryApiResponse {
  status: number;
  data: {
    status: string;
    data: {
      data: SummaryRecord[];
      pagination: { offset: number; count: number; nextToken?: string | null };
      totalRecords?: number;
    };
  };
}

type SupplierRow = {
  // isGroup: false;
  supplier: string;
  category: { text: string; icon: React.ReactNode };
  records: string | number;
  responseTime: string;
  health: { text: string; color: string };
  history: string;
  hasData: boolean;
};

// Helper function to normalize supplier keys
  const normalizeSupplierKey = (label: string): string => {
    const map: Record<string, string> = {
    "Mondee (API)": "Mondee-API",
    Mystifly: "Mystifly",
    Duffel: "Duffel",
    "Mondee (Amadeus)": "Mondee-Amadeus",
    Voyzant: "Voyzant",
    Wsap: "Wsap",
    Overall: "Overall",
    Nuitee: "Nuitee",
    "Hotel Trader": "Hotel-Trader",
    "Hotel Overall": "Hotel Overall",
    };
    return map[label] || label.replace(/\s+/g, "-");
  };

const supplierConfigs: Record<
  string,
  { product: string; supplierId: number; supplierName: string; supplierSource: string }
> = {
  "Mondee-API": { product: "Flight", supplierId: 1, supplierName: "Mondee", supplierSource: "API" },
  Mystifly: { product: "Flight", supplierId: 2, supplierName: "Mystifly", supplierSource: "API" },
  Duffel: { product: "Flight", supplierId: 3, supplierName: "Duffel", supplierSource: "API" },
  "Mondee-Amadeus": { product: "Flight", supplierId: 0, supplierName: "Mondee", supplierSource: "AMADEUS" },
  Voyzant: { product: "Flight", supplierId: 0, supplierName: "Voyzant", supplierSource: "Amadeus" },
  Wsap: { product: "Flight", supplierId: 0, supplierName: "Wsap", supplierSource: "Amadeus" },
  Overall: { product: "Flight", supplierId: 999, supplierName: "Overall", supplierSource: "ALL" },
  Nuitee: { product: "Hotel", supplierId: 0, supplierName: "Nuitee", supplierSource: "API" },
  "Hotel-Trader": { product: "Hotel", supplierId: 1, supplierName: "Hotel Trader", supplierSource: "API" },
  "Hotel Overall": { product: "Hotel", supplierId: 999, supplierName: "Overall", supplierSource: "ALL" },
};

// Utility functions
const formatValue = (val: any): string | number => 
  (val === null || val === undefined || isNaN(val) ? "-" : val);
const formatSeconds = (ms: number | undefined): string =>
  typeof ms === "number" && !isNaN(ms) ? (ms / 1000).toFixed(2) + " s" : "-";
const mapHealth = (code: string | undefined): { text: string; color: string } => {
  if (!code) return { text: "-", color: "inherit" };
  switch (code.toUpperCase()) {
    case "A":
      return { text: "Amber", color: "orange" };
    case "R":
      return { text: "Red", color: "red" };
    case "G":
      return { text: "Green", color: "green" };
    default:
      return { text: code, color: "inherit" };
  }
};

// const getSupplierRecord = (arr: SummaryRecord[], supplierKey: string): SummaryRecord | undefined => {
//   const config = supplierConfigs[supplierKey];
//   if (!config) return undefined;

//   return arr.find(
//     (r) =>
//       r.supplierName?.toLowerCase() === config.supplierName.toLowerCase() &&
//       r.supplierSource?.toLowerCase() === config.supplierSource.toLowerCase()
//   );
// };

const getSupplierRecord = (arr: SummaryRecord[], supplierKey: string) => {
const cfg = supplierConfigs[supplierKey];
if (!cfg) return undefined;
return arr.find(
(r) => r.supplierName?.toLowerCase() === cfg.supplierName.toLowerCase() && r.supplierSource?.toLowerCase() === cfg.supplierSource.toLowerCase()
);
};


// Main component
export default function MonitoringTool() {
  // const firstOpen = useRef(true);
  const [redCountsState, setRedCountsState] = useState<Record<string, number>>({});
  const [flightData, setFlightData] = useState<SummaryRecord[]>([]);
  const [hotelData, setHotelData] = useState<SummaryRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // const [mondeeOption, setMondeeOption] = useState<"API" | "Amadeus">("API");
  
  const [openHistory, setOpenHistory] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<string | null>(null);

  const [historyData, setHistoryData] = useState<SummaryRecord[]>([]);
  // const [page, setPage] = useState(0);
  // const [rowsPerPage, setRowsPerPage] = useState(10);
  // const [totalCount, setTotalCount] = useState(0);
  // const [nextToken, setNextToken] = useState<string | null>(null);
  const [historyVisible, setHistoryVisible] = useState<SummaryRecord[]>([]);
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [fetchingMore, setFetchingMore] = useState(false);

  const firstOpen = useRef(true);
  const scrollTimeout = useRef<any>(null);

  // tone handling
    const redCounts = useRef<Record<string, number>>({});
    const lastSnapshot = useRef<Record<string, SummaryRecord>>({});
    const continuousInterval = useRef<NodeJS.Timeout | null>(null);
    const [continuousPopup, setContinuousPopup] = useState(false);
    const isInitialLoad = useRef(true);
    const [popupSupplier, setPopupSupplier] = useState<string | null>(null);
    


  const suppliers = [
    { id: "Mondee-API", label: "Mondee (API)", product: "Flight" },
    { id: "Mystifly", label: "Mystifly", product: "Flight" },
    { id: "Duffel", label: "Duffel", product: "Flight" },
    { id: "Mondee-Amadeus", label: "Mondee (Amadeus)", product: "Flight" },
    { id: "Voyzant", label: "Voyzant", product: "Flight" },
    { id: "Wsap", label: "Wsap", product: "Flight" },
    { id: "Overall", label: "Overall", product: "Flight" },
    { id: "Nuitee", label: "Nuitee", product: "Hotel" },
    { id: "Hotel-Trader", label: "Hotel Trader", product: "Hotel" },
    { id: "Hotel Overall", label: "Hotel Overall", product: "Hotel" },
  ];

  // ---- sound helpers ----
  const playOnce = () => {
    const audio = new Audio("/sounds/alert.mp3");
    audio.play().catch(() => {});
  };

const playMultiple = (times: number) => {
    let count = 0;
    const audio = new Audio("/sounds/alert.mp3");
    const playNext = () => {
      if (count < times) {
        audio.currentTime = 0;
        audio.play().catch(() => {});
        count++;
        setTimeout(playNext, 1000);
      }
    };
    playNext();
  };

// Start continuous tone
const startContinuous = (supplier: string) => {
    if (continuousInterval.current) return;
    setPopupSupplier(supplier);
    setContinuousPopup(true);
    continuousInterval.current = setInterval(() => {
      const audio = new Audio("/sounds/alert.mp3");
      audio.play().catch(() => {});
    }, 1000);
  };

// Stop continuous tone
const stopContinuous = () => {
    if (continuousInterval.current) {
      clearInterval(continuousInterval.current);
      continuousInterval.current = null;
    }
    setContinuousPopup(false);
  };

  const fetchSummary = async () => {
    setLoading(true);
    setError("");
    try {
      const res = (await getMonitoringSummary()) as SummaryResponse | null;
      if (res?.status === 200 && res?.data?.data) {
        setFlightData(res.data.data.flight || []);
        setHotelData(res.data.data.hotel || []);
        // const summary = res.data.data;
        // const mondeeFiltered = summary.flight.filter(
        //   (f) => 
        //     f.supplierName.toLowerCase() === "mondee" && 
        //     f.supplierSource.toLowerCase() === mondeeOption.toLowerCase()
        // );
        // const otherFlights = summary.flight.filter(
        //   (f) => f.supplierName.toLowerCase() !== "mondee"
        // );
        // setFlightData([...mondeeFiltered, ...otherFlights]);
        // setHotelData(summary.hotel || []);
      } else {
        setError("Unexpected response format.");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to fetch monitoring summary.");
    } finally {
      setLoading(false);
    }
  };

   // ---- run tone logic whenever data changes ----
    useEffect(() => {
    const allSuppliers = [...flightData, ...hotelData];
  
    allSuppliers.forEach((s) => {
      const key = `${s.supplierName}-${s.supplierSource}`;
  
      if (s.health?.toUpperCase() === "R") {
        const prev = lastSnapshot.current[key];
        const hasNewData =
          !prev ||
          (s.supplierName === "Overall"
      ? false //prev.health !== s.health  only compare health for Overall
      : prev.records !== s.records || prev.responseTime !== s.responseTime);
  
        if (isInitialLoad.current) {
          // On very first load: just save snapshot, no tone
          redCounts.current[key] = 0;
        } else if (hasNewData) {
          // Only increment when new data comes
          redCounts.current[key] = (redCounts.current[key] || 0) + 1;
          setRedCountsState({ ...redCounts.current }); // <-- add this line
  
          const count = redCounts.current[key];
  
          // if (count === 1) {
          //   playOnce();
          // } else if (count === 2) {
          //   playMultiple(3);
          // } else if (count === 3) {
          //   if (s.supplierName === "Overall") {
          //     if (!skippedOverallOnce.current) {
          //       skippedOverallOnce.current = true;
          //     } else {
          //       startContinuous(s.supplierName);
          //     }
          //   } else {
          //     startContinuous(s.supplierName);
          //   }
          // } 
  
          if (s.supplierName !== "Overall") {
            // only trigger sounds for individual suppliers
            if (count === 1) {
              playOnce();
            } else if (count === 2) {
              playMultiple(3);
            } else if (count === 3) {
              startContinuous(s.supplierName);
            }
          }
        }
  
        // Save snapshot
        lastSnapshot.current[key] = { ...s };
      } else {
        // Reset when not Red
        redCounts.current[key] = 0;
        lastSnapshot.current[key] = { ...s };
      }
    });
  
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
    }
  }, [flightData, hotelData]);

  useEffect(() => {
    fetchSummary();
    const interval = setInterval(fetchSummary, 3600000); // 1 hour
    return () => clearInterval(interval);
  }, []);

  const buildRows = (): SupplierRow[] => {
    return suppliers.map((s) => {
      const data = s.product === "Hotel" ? hotelData : flightData;
      const record = getSupplierRecord(data, s.id);
      return {
        supplierId: s.id,
        supplier: s.label,
        category: {
          text: s.product,
          icon: s.product === "Flight" ? <FlightIcon fontSize="small" className="categoryFlightIcon" /> : <HotelIcon fontSize="small" />,
        },
        // records: formatValue(record?.records),
        records: record?.records !== undefined ? Math.floor(record.records) : "-",
        responseTime: formatSeconds(record?.responseTime),
        health: mapHealth(record?.health),
        history: "View History",
        hasData: !!record,
      };
    });
  };

  const rows = buildRows();

  const fetchHistory = async (supplierKey: string) => {
    if (!supplierKey || fetchingMore) return;
  setFetchingMore(true);

    const supplier = supplierConfigs[supplierKey];
    if (!supplierKey) return;
    setFetchingMore(true);
    try {
      const cfg = supplierConfigs[supplierKey];
      if (!cfg) return;
      const requestBody = {
      product: cfg.product,
      supplierId: cfg.supplierId,
      supplierName: cfg.supplierName,
      supplierSource: cfg.supplierSource,
      pagination: { offset: 0, count: 99999 }, // request large count so server returns full history if it supports it
    };

      console.log("Sending request:", requestBody);

      const res = (await getMonitoringHistory(requestBody)) as HistoryApiResponse | null;

      console.log("Full API Response:", res);

      const apiData: SummaryRecord[] = res?.data?.data?.data || [];
    // const pagination = res.data?.data?.data?.pagination || { nextToken: null };

    setHistoryData(apiData);
    setVisibleCount(ITEMS_PER_PAGE);
    setHistoryVisible(apiData.slice(0, ITEMS_PER_PAGE));
    // setTotalCount(res.data?.data?.data?.totalRecords ?? apiData.length);
    // setHistoryData((prev) => (isFirstLoad ? apiData : [...prev, ...apiData]));
    // setNextToken(pagination.nextToken ?? null);
      
    } catch (error: any) {
      console.error("Fetch error:", error.response ? error.response.data : error.message);
      setHistoryData([]);
      setHistoryVisible([]);
      // setTotalCount(0);
      // setNextToken(null);
    }

    finally {
      // setLoading(false);
      setFetchingMore(false);
    }
  };

  const openHistoryPopup = async (supplierKey: string) => {
setSelectedSupplier(supplierKey);
setHistoryData([]);
setHistoryVisible([]);
setVisibleCount(ITEMS_PER_PAGE);
firstOpen.current = true;
setOpenHistory(true);
await fetchHistory(supplierKey);
};


const handleCloseHistory = () => {
setOpenHistory(false);
setSelectedSupplier(null);
setHistoryData([]);
setHistoryVisible([]);
setVisibleCount(ITEMS_PER_PAGE);
firstOpen.current = true;
};

// Load more visible items from historyAll
const loadMoreVisible = () => {
if (fetchingMore) return;
if (visibleCount >= historyData.length) return;
setFetchingMore(true);
// small timeout to show spinner visibly
setTimeout(() => {
const next = Math.min(historyData.length, visibleCount + ITEMS_PER_PAGE);
setHistoryVisible(historyData.slice(0, next));
setVisibleCount(next);
setFetchingMore(false);
}, 200);
};

// Scroll handler attached to DialogContent
const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
// prevent the first automatic scroll fired when modal opens from triggering load
if (firstOpen.current) {
firstOpen.current = false;
return;
}


const target = e.currentTarget;
const { scrollTop, scrollHeight, clientHeight } = target;


if (scrollTop + clientHeight >= scrollHeight - 40) {
// debounce
if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
scrollTimeout.current = setTimeout(() => {
loadMoreVisible();
}, 150);
}
};
 



 
 
   // pagination
  //  const handleChangePage = async (_: unknown, newPage: number) => {
  //    setPage(newPage);
  //    if (selectedSupplier) {
 
  //      const offset = newPage * rowsPerPage; // FIX: proper offset
 
  //      await fetchHistory(selectedSupplier, offset, rowsPerPage, nextToken);
 
  //    }
  //  };
 
  //  const handleChangeRowsPerPage = async (event: React.ChangeEvent<HTMLInputElement>) => {
  //    const newSize = parseInt(event.target.value, 10);
  //    setRowsPerPage(newSize);
  //    setPage(0);
  //    if (selectedSupplier) {
  //      await fetchHistory(selectedSupplier, 0, newSize, null);
  //    }
  //  };

   const getHealthColor = (health: string): { textColor: string; bgColor: string; borderColor: string } => {
  switch (health) {
    case "Red":
      return { textColor: "#dc2626", bgColor: "#fee2e2", borderColor: "#fecaca" };
    case "Amber":
      return { textColor: "#d97706", bgColor: "#fef3c7", borderColor: "#fde68a" };
    case "Green":
      return { textColor: "#059669", bgColor: "#d1fae5", borderColor: "#a7f3d0" };
    default:
      return { textColor: "inherit", bgColor: "inherit", borderColor: "inherit" };
  }
};



  return (
    <>
      <Box className="headingBtnWrap">
        <Typography variant="h1">Monitoring Tool</Typography>
      </Box>

      
    <Paper sx={{ width: "100%", overflowX: "auto", marginTop: "20px", position: "relative" }}>
      <Box className="pageLoading">
        {loading && <CircularProgress />}
        {error && <Typography color="error">{error}</Typography>}
      </Box>
      <TableContainer sx={{ maxHeight: 540 }} className="monitoringWrap">
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell align="left" sx={{ width: 250, pl: 4 }}>Supplier</TableCell>
              <TableCell align="center">Category</TableCell>
              <TableCell align="center">No. of Records</TableCell>
              <TableCell align="center">Response Time</TableCell>
              <TableCell align="center">Health Status</TableCell>
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row: SupplierRow, idx: number) => {
              let responseCellStyle: React.CSSProperties = {};
              // Blinking logic for "Red" health
              if (row.health.text === "Red") {
                responseCellStyle = {
                  fontWeight: "bold",
                  color: "#dc2626",
                };
              } else if (row.health.text === "Amber") {
                responseCellStyle = { fontWeight: "bold", color: "#d97706" };
              } else if (row.health.text === "Green") {
                responseCellStyle = { fontWeight: "bold", color: "#059669" };
              }

              return (
                <TableRow key={idx}>
                  <TableCell align="left" sx={{ width: 250 }}>
                    {row.supplier}
                  </TableCell>
                  <TableCell align="center">
                    <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 5,
                          fontWeight: "bold",
                          bgcolor: row.category.text === "Flight" ? "#e0f2fe" : "#f3e8ff",
                          color: row.category.text === "Flight" ? "#0369a1" : "#7e22ce",
                          borderColor: row.category.text === "Flight" ? "#bae6fd" : "#e9d5ff",
                        }}
                      >
                        {row.category.icon} {row.category.text}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
  {row.records !== "-" && row.records !== null && row.records !== undefined ? (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      <SourceIcon fontSize="small" className="sourceIcon" />
      {row.records}
    </Box>
  ) : (
    "-"
  )}
</TableCell>
                  <TableCell align="center">
  {row.responseTime !== "-" ? (
    <Box
      sx={{ 
        display: "inline-flex", 
        alignItems: "center", 
        gap: 0.8, 
        ...responseCellStyle  // apply styles here
      }}
    >
      <AccessTimeIcon fontSize="small" className="sourceIcon" />
      {row.responseTime}
    </Box>
  ) : (
    "-"
  )}
</TableCell>

                  <TableCell align="center">
                    <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.8,
      px: 1.5,
      py: 0.5,
      borderRadius: 5,
      fontWeight: "bold",
      bgcolor: getHealthColor(row.health.text).bgColor,
      color: getHealthColor(row.health.text).textColor,
      borderColor: getHealthColor(row.health.text).borderColor,
    }}
  >
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        bgcolor: getHealthColor(row.health.text).textColor,
        animation: "pulseAnim 5s ease-in-out infinite",
        "@keyframes pulseAnim": {
          "0%": { transform: "scale(1)", opacity: 1 },
          "50%": { transform: "scale(0.8)", opacity: 0.3 },
          "100%": { transform: "scale(1)", opacity: 1 },
        },
      }}
    />
    {row.health.text}
  </Box>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      // onClick={() => row.hasData && openHistoryPopup(row.supplier)}
                      // onClick={() => row.hasData && openHistoryPopup(row.supplier.replace(" ", "-"))}
                      onClick={() => {
  if (row.hasData) {
    const normalized = normalizeSupplierKey(row.supplier);
    openHistoryPopup(normalized);
  }
}}

                      disabled={!row.hasData}
                      className="viewBtn"
                    >
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      <HistoryIcon fontSize="small" />
      {row.history}
    </Box>
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>

      <Dialog open={openHistory} onClose={handleCloseHistory} fullWidth maxWidth="sm">
        <DialogTitle>
          History for {selectedSupplier} {fetchingMore && <CircularProgress className="smallCircleProgress" />}
            <CancelIcon onClick={() => setOpenHistory(false)} className="closeModal" />
        </DialogTitle>
        <DialogContent
          onScroll={handleScroll}
          style={{ maxHeight: 400, overflowY: "auto" }}
        >
            {historyVisible.length === 0 && !fetchingMore && (
            <Typography align="center">No history available.</Typography>
          )}
          {historyVisible.length > 0 && (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">S.No.</TableCell>
                    <TableCell align="center">No. of Records</TableCell>
                    <TableCell align="center">Response Time</TableCell>
                    <TableCell align="center">Health</TableCell>
                    <TableCell align="center">Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyVisible.map((h, i) => (
                    <TableRow key={`${h.createdAt}-${i}`}>
                      <TableCell align="center">{i + 1}</TableCell>
                      <TableCell align="center">{h.records !== undefined ? Math.floor(h.records) : "-"}</TableCell>
                      <TableCell align="center">{formatSeconds(h.responseTime)}</TableCell>
                      <TableCell align="center">{mapHealth(h.health).text}</TableCell>
                      <TableCell align="center">
                          {h.createdAt
                          ? new Date(h.createdAt).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {/* show a small loader while more items are being appended */}
{fetchingMore && <Box textAlign="center" p={2}><CircularProgress size={20} /></Box>}


{/* optional: show a small message when all items loaded */}
{!fetchingMore && visibleCount >= historyData.length && historyData.length > 0 && (
<Box textAlign="center" p={1}>
<Typography variant="body2">End of history</Typography>
</Box>
)}
              {/* <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              /> */}
            </>
          )}
          {/* {fetchingMore && <Box textAlign="center" p={2}><CircularProgress size={20} /></Box>} */}
        </DialogContent>
      </Dialog>
       {/* Continuous Tone Stop Popup */}
      <Dialog open={continuousPopup} onClose={stopContinuous} className="PopupAlertMsg">
        <DialogTitle>Alert</DialogTitle>
        <DialogContent>
         <Typography variant="body1" gutterBottom>
            Continuous alert is playing because supplier 
            <strong> {popupSupplier} </strong> is Red.
          </Typography>
          <button
            onClick={stopContinuous}
            style={{
              background: "#b71c1c",
              color: "#fff",
              padding: "8px 16px",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              marginTop: "15px",
            }}
          >
            STOP
          </button>
        </DialogContent>
      </Dialog>
      {/* <Paper sx={{ width: "100%", overflowX: "auto", mt: 3 }}>
        <Typography variant="h6" sx={{ p: 2 }}>
          Red Counts Summary
        </Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Supplier</TableCell>
              <TableCell align="center">Red Count</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {["Mondee", "Voyzant", "Mystifly", "Overall", "Nuitee"].map((supplier) => {
              const matches = Object.keys(redCountsState).filter((k) =>
                k.toLowerCase().includes(supplier.toLowerCase())
              );
              const total = matches.reduce((sum, k) => sum + (redCountsState[k] || 0), 0);
      
              return (
                <TableRow key={supplier}>
                  <TableCell>{supplier}</TableCell>
                  <TableCell align="center">{total}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper> */}
    </>
  );
}
